import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ChevronLeft, Download, Play, Printer, Search } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { useFleetStore } from '@/stores/fleet';
import { cn } from '@/lib/utils';

export type Preset = 'today' | 'yesterday' | '7d' | '30d' | 'custom';

const DAY_MS = 24 * 60 * 60 * 1000;
/** Máximo permitido por relatório customizado. */
export const CUSTOM_MAX_RANGE_DAYS = 30;
/** Quão longe no passado o usuário pode escolher como início. */
export const CUSTOM_MAX_BACK_DAYS = 180;

export function rangeForPreset(
  preset: Preset,
  customFrom?: string,
  customTo?: string,
): { from: string; to: string } {
  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  switch (preset) {
    case 'today':
      return { from: startOfToday.toISOString(), to: now.toISOString() };
    case 'yesterday': {
      const start = new Date(startOfToday.getTime() - DAY_MS);
      return { from: start.toISOString(), to: startOfToday.toISOString() };
    }
    case '7d': {
      const start = new Date(now.getTime() - 7 * DAY_MS);
      return { from: start.toISOString(), to: now.toISOString() };
    }
    case 'custom': {
      // Datas vindas do input <input type="date"> são YYYY-MM-DD em hora local.
      // From = 00:00:00 local; To = 23:59:59 local pra fechar o dia inteiro.
      const f = customFrom
        ? new Date(`${customFrom}T00:00:00`)
        : new Date(now.getTime() - 7 * DAY_MS);
      const t = customTo
        ? new Date(`${customTo}T23:59:59`)
        : now;
      return { from: f.toISOString(), to: t.toISOString() };
    }
    case '30d':
    default: {
      const start = new Date(now.getTime() - 30 * DAY_MS);
      return { from: start.toISOString(), to: now.toISOString() };
    }
  }
}

export const PRESET_LABEL: Record<Preset, string> = {
  today: 'Hoje',
  yesterday: 'Ontem',
  '7d': 'Últimos 7 dias',
  '30d': 'Últimos 30 dias',
  custom: 'Customizado',
};

/** Hoje (00:00) no formato YYYY-MM-DD pro <input type="date">. */
function todayIso(): string {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).toISOString().slice(0, 10);
}

function daysBackIso(days: number): string {
  const d = new Date();
  const base = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  return new Date(base.getTime() - days * DAY_MS).toISOString().slice(0, 10);
}

/**
 * Valida o intervalo customizado. Retorna null se válido, ou uma mensagem
 * de erro pra mostrar no formulário.
 */
export function validateCustomRange(from: string, to: string): string | null {
  if (!from || !to) return 'Selecione data inicial e final.';
  const f = new Date(`${from}T00:00:00`);
  const t = new Date(`${to}T23:59:59`);
  if (Number.isNaN(f.getTime()) || Number.isNaN(t.getTime())) return 'Data inválida.';
  if (f > t) return 'Data inicial precisa ser anterior à data final.';
  const todayEnd = new Date();
  todayEnd.setHours(23, 59, 59, 999);
  if (t > todayEnd) return 'Data final não pode ser no futuro.';
  const limitBack = new Date();
  limitBack.setHours(0, 0, 0, 0);
  limitBack.setTime(limitBack.getTime() - CUSTOM_MAX_BACK_DAYS * DAY_MS);
  if (f < limitBack) return `Início não pode passar de ${CUSTOM_MAX_BACK_DAYS} dias atrás.`;
  const rangeDays = (t.getTime() - f.getTime()) / DAY_MS;
  if (rangeDays > CUSTOM_MAX_RANGE_DAYS) {
    return `Máximo de ${CUSTOM_MAX_RANGE_DAYS} dias por relatório (selecionado: ${Math.ceil(rangeDays)}).`;
  }
  return null;
}

/**
 * Snapshot dos filtros que foi efetivamente "submetido" pra geração do
 * relatório. A query só executa quando isso é não-nulo.
 */
export interface ExecutedFilters {
  deviceId: number | 'all' | null;
  ids: number[];
  preset: Preset;
  range: { from: string; to: string };
}

/**
 * Gerencia os filtros e o ciclo de "preencher → gerar". A request só
 * dispara quando `executed` muda (após `generate()`); mudar filtros sem
 * clicar não dispara nada.
 */
export function useReportFilters(deviceMode: 'single' | 'multi') {
  const devices = useFleetStore((s) => s.devices);
  const sortedDevices = useMemo(
    () => Array.from(devices.values()).sort((a, b) => a.name.localeCompare(b.name, 'pt-BR')),
    [devices],
  );

  // deep-link: /reports/...?device=ID pré-seleciona a embarcação (vindo do Detalhe).
  const [searchParams] = useSearchParams();
  const deviceParam = searchParams.get('device');
  const deviceParamId = deviceParam && Number.isFinite(Number(deviceParam)) ? Number(deviceParam) : null;

  const initialDevice =
    deviceMode === 'single'
      ? (deviceParamId ?? sortedDevices[0]?.id ?? null)
      : ('all' as const);
  const [deviceId, setDeviceId] = useState<number | 'all' | null>(initialDevice);
  const [preset, setPreset] = useState<Preset>('today');
  const [customFrom, setCustomFrom] = useState<string>(() => daysBackIso(7));
  const [customTo, setCustomTo] = useState<string>(() => todayIso());
  const [executed, setExecuted] = useState<ExecutedFilters | null>(null);

  const range = useMemo(
    () => rangeForPreset(preset, customFrom, customTo),
    [preset, customFrom, customTo],
  );
  const customError = useMemo(
    () => (preset === 'custom' ? validateCustomRange(customFrom, customTo) : null),
    [preset, customFrom, customTo],
  );

  if (deviceMode === 'single' && deviceId === null && sortedDevices[0]) {
    setDeviceId(sortedDevices[0].id);
  }

  const generate = () => {
    if (preset === 'custom' && customError) return;
    const ids =
      deviceId === 'all'
        ? sortedDevices.map((d) => d.id)
        : typeof deviceId === 'number'
          ? [deviceId]
          : [];
    if (ids.length === 0) return;
    setExecuted({
      deviceId,
      ids,
      preset,
      range: rangeForPreset(preset, customFrom, customTo),
    });
  };

  return {
    devices,
    sortedDevices,
    deviceId,
    setDeviceId,
    preset,
    setPreset,
    customFrom,
    setCustomFrom,
    customTo,
    setCustomTo,
    customError,
    range,
    executed,
    generate,
  };
}

interface ShellProps {
  title: string;
  description?: string;
  children: ReactNode;
}

export function ReportShell({ title, description, children }: ShellProps) {
  return (
    <div className="space-y-4 p-4 sm:p-6">
      <Link
        to="/reports"
        className="-ml-2 inline-flex items-center gap-0.5 rounded-md px-2 py-1 text-sm font-medium text-primary hover:bg-primary/5"
      >
        <ChevronLeft className="h-5 w-5" />
        Voltar
      </Link>
      <div>
        <h1 className="text-xl font-bold">{title}</h1>
        {description && <p className="text-sm text-muted-foreground">{description}</p>}
      </div>
      {children}
    </div>
  );
}

interface FiltersProps {
  deviceMode: 'single' | 'multi';
  deviceId: number | 'all' | null;
  onDeviceChange: (v: number | 'all') => void;
  preset: Preset;
  onPresetChange: (p: Preset) => void;
  onGenerate: () => void;
  generating?: boolean;
  extra?: ReactNode;
  customFrom?: string;
  customTo?: string;
  onCustomFromChange?: (v: string) => void;
  onCustomToChange?: (v: string) => void;
  customError?: string | null;
}

export function ReportFilters({
  deviceMode,
  deviceId,
  onDeviceChange,
  preset,
  onPresetChange,
  onGenerate,
  generating,
  extra,
  customFrom,
  customTo,
  onCustomFromChange,
  onCustomToChange,
  customError,
}: FiltersProps) {
  const devices = useFleetStore((s) => s.devices);
  const sorted = useMemo(
    () => Array.from(devices.values()).sort((a, b) => a.name.localeCompare(b.name, 'pt-BR')),
    [devices],
  );
  const [deviceSearch, setDeviceSearch] = useState('');
  const filteredDevices = useMemo(() => {
    const q = deviceSearch.trim().toLowerCase();
    if (!q) return sorted;
    return sorted.filter(
      (d) =>
        d.name.toLowerCase().includes(q) ||
        (d.model ?? '').toLowerCase().includes(q) ||
        (d.uniqueId ?? '').toLowerCase().includes(q),
    );
  }, [sorted, deviceSearch]);
  const selectedDevice =
    typeof deviceId === 'number' ? devices.get(deviceId) : null;
  const baseCanGenerate =
    deviceMode === 'multi' ? sorted.length > 0 : typeof deviceId === 'number';
  const canGenerate = baseCanGenerate && !(preset === 'custom' && customError);

  // Quando o usuário digita na busca, a opção "Todas" some do select.
  // Se o filtro estava em 'all', o select passa a exibir a primeira embarcação
  // filtrada visualmente, mas o estado interno continua em 'all' — e o
  // relatório acaba pedindo todas. Aqui sincronizamos: se há busca ativa
  // e o filtro é 'all', auto-selecionamos a primeira embarcação filtrada.
  useEffect(() => {
    if (
      deviceMode === 'multi' &&
      deviceId === 'all' &&
      deviceSearch.trim() !== '' &&
      filteredDevices.length > 0
    ) {
      onDeviceChange(filteredDevices[0].id);
    }
  }, [deviceMode, deviceId, deviceSearch, filteredDevices, onDeviceChange]);

  const maxDateAttr = useMemo(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), d.getDate()).toISOString().slice(0, 10);
  }, []);
  const minDateAttr = useMemo(() => {
    const d = new Date();
    const base = new Date(d.getFullYear(), d.getMonth(), d.getDate());
    return new Date(base.getTime() - CUSTOM_MAX_BACK_DAYS * 24 * 60 * 60 * 1000)
      .toISOString()
      .slice(0, 10);
  }, []);

  return (
    <Card>
      <CardContent className="space-y-3 p-4">
        <div className={cn('grid gap-3', extra ? 'sm:grid-cols-3' : 'sm:grid-cols-2')}>
          <label className="space-y-1">
            <span className="text-xs font-semibold text-muted-foreground">Embarcação</span>
            <div className="space-y-1.5">
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={deviceSearch}
                  onChange={(e) => setDeviceSearch(e.target.value)}
                  placeholder={
                    selectedDevice
                      ? `Selecionada: ${selectedDevice.name} — digite para filtrar`
                      : 'Buscar por nome, modelo ou IMEI'
                  }
                  className="h-9 pl-9 text-xs"
                />
              </div>
              <select
                value={deviceId === null ? '' : String(deviceId)}
                onChange={(e) =>
                  onDeviceChange(e.target.value === 'all' ? 'all' : Number(e.target.value))
                }
                className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
              >
                {deviceMode === 'multi' && deviceSearch.trim() === '' && (
                  <option value="all">Todas</option>
                )}
                {filteredDevices.length === 0 && (
                  <option value="" disabled>
                    Nenhuma embarcação corresponde
                  </option>
                )}
                {filteredDevices.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name}
                  </option>
                ))}
              </select>
            </div>
          </label>
          <label className="space-y-1">
            <span className="text-xs font-semibold text-muted-foreground">Período</span>
            <select
              value={preset}
              onChange={(e) => onPresetChange(e.target.value as Preset)}
              className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
            >
              {Object.entries(PRESET_LABEL).map(([k, v]) => (
                <option key={k} value={k}>
                  {v}
                </option>
              ))}
            </select>
          </label>
          {extra}
        </div>

        {preset === 'custom' && (
          <div className="space-y-2 rounded-md border border-border bg-secondary/30 p-3">
            <div className="grid gap-3 sm:grid-cols-2">
              <label className="space-y-1">
                <span className="text-xs font-semibold text-muted-foreground">De</span>
                <Input
                  type="date"
                  value={customFrom ?? ''}
                  min={minDateAttr}
                  max={maxDateAttr}
                  onChange={(e) => onCustomFromChange?.(e.target.value)}
                  className="h-9"
                />
              </label>
              <label className="space-y-1">
                <span className="text-xs font-semibold text-muted-foreground">Até</span>
                <Input
                  type="date"
                  value={customTo ?? ''}
                  min={minDateAttr}
                  max={maxDateAttr}
                  onChange={(e) => onCustomToChange?.(e.target.value)}
                  className="h-9"
                />
              </label>
            </div>
            <p className="text-[11px] text-muted-foreground">
              Até {CUSTOM_MAX_RANGE_DAYS} dias por relatório, recuando no máximo {CUSTOM_MAX_BACK_DAYS} dias.
            </p>
            {customError && (
              <p className="text-[11px] font-semibold text-destructive">{customError}</p>
            )}
          </div>
        )}

        <button
          type="button"
          onClick={onGenerate}
          disabled={!canGenerate || generating}
          className={cn(
            'inline-flex w-full items-center justify-center gap-2 rounded-md bg-primary px-4 py-2.5',
            'text-sm font-semibold text-primary-foreground shadow-sm transition-colors',
            'hover:bg-primary/90 disabled:opacity-50 sm:w-auto',
          )}
        >
          <Play className="h-4 w-4" />
          {generating ? 'Gerando…' : 'Gerar Relatório'}
        </button>
      </CardContent>
    </Card>
  );
}

interface ResultBarProps {
  loading: boolean;
  count: number;
  unit?: string;
  onExport: () => void;
  disabled?: boolean;
}

export function ResultBar({ loading, count, unit = 'linhas', onExport, disabled }: ResultBarProps) {
  return (
    <div className="flex items-center justify-between">
      <p className="text-sm text-muted-foreground">
        {loading
          ? 'Carregando…'
          : `${count.toLocaleString('pt-BR')} ${count === 1 ? unit.replace(/s$/, '') : unit}`}
      </p>
      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => window.print()}
          disabled={disabled || loading || count === 0}
          className="inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-xs font-semibold text-foreground hover:bg-accent disabled:opacity-50"
          title="Imprimir / salvar em PDF"
        >
          <Printer className="h-3.5 w-3.5" />
          PDF
        </button>
        <button
          type="button"
          onClick={onExport}
          disabled={disabled || loading || count === 0}
          className="inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-xs font-semibold text-foreground hover:bg-accent disabled:opacity-50"
        >
          <Download className="h-3.5 w-3.5" />
          CSV
        </button>
      </div>
    </div>
  );
}

export function exportCsv(filename: string, header: string[], rows: (string | number | null | undefined)[][]) {
  const escape = (cell: string | number | null | undefined) => {
    const s = cell == null ? '' : String(cell);
    return /[",;\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const csv = [header, ...rows].map((r) => r.map(escape).join(';')).join('\n');
  const blob = new Blob([`﻿${csv}`], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export function ReportTable({
  loading,
  error,
  empty,
  notRun,
  children,
}: {
  loading: boolean;
  error: unknown;
  empty: boolean;
  notRun: boolean;
  children: ReactNode;
}) {
  return (
    <Card>
      <CardContent className="p-0">
        {notRun ? (
          <p className="p-6 text-center text-sm text-muted-foreground">
            Selecione os filtros e clique em <strong className="font-semibold">Gerar Relatório</strong>.
          </p>
        ) : loading ? (
          <p className="p-6 text-sm text-muted-foreground">Carregando…</p>
        ) : error ? (
          <p className="p-6 text-sm text-muted-foreground">
            Ainda não temos um relatório pronto para entregar nesse momento. Tente novamente em 24 horas.
          </p>
        ) : empty ? (
          <p className="p-6 text-sm text-muted-foreground">Sem dados no período.</p>
        ) : (
          <div className="overflow-x-auto">{children}</div>
        )}
      </CardContent>
    </Card>
  );
}

export function formatDuration(ms: number): string {
  if (!Number.isFinite(ms) || ms <= 0) return '0min';
  const totalMin = Math.floor(ms / 60000);
  const days = Math.floor(totalMin / (60 * 24));
  const hours = Math.floor((totalMin - days * 24 * 60) / 60);
  const minutes = totalMin - days * 24 * 60 - hours * 60;
  if (days > 0) return `${days}d ${hours}h ${String(minutes).padStart(2, '0')}min`;
  return `${hours}h ${String(minutes).padStart(2, '0')}min`;
}

/** Mesma duração porém em horas:minutos puro (sem split em dias). */
export function formatTotalHours(ms: number): string {
  if (!Number.isFinite(ms) || ms <= 0) return '0 horas e 00 minutos';
  const totalMin = Math.floor(ms / 60000);
  const hours = Math.floor(totalMin / 60);
  const minutes = totalMin - hours * 60;
  return `${hours.toLocaleString('pt-BR')} horas e ${String(minutes).padStart(2, '0')} minutos`;
}

export function metersToKm(m?: number | null): string {
  if (m == null || !Number.isFinite(m)) return '—';
  return `${(m / 1000).toLocaleString('pt-BR', { maximumFractionDigits: 2 })} km`;
}

export function knotsToKmh(k?: number | null): string {
  if (k == null || !Number.isFinite(k)) return '—';
  return `${Math.round(k * 1.852)} km/h`;
}
